import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import Topbar from "../../components/common/Topbar";
import Toast from "../../components/common/Toast";
import { fmtUSD } from "../../utils/formatters";
import api from "../../services/api";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
} from "recharts";

// Custom hook for responsive values
const useResponsive = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return {
    width: windowSize.width,
    height: windowSize.height,
    xs: windowSize.width < 480,
    sm: windowSize.width >= 480 && windowSize.width < 768,
    md: windowSize.width >= 768 && windowSize.width < 1024,
    lg: windowSize.width >= 1024 && windowSize.width < 1280,
    xl: windowSize.width >= 1280,
    isMobile: windowSize.width < 768,
    isTablet: windowSize.width >= 768 && windowSize.width < 1024,
    isDesktop: windowSize.width >= 1024,
  };
};

const Analytics = () => {
  const { historyOrders, balance } = useSelector((state) => state.trade);
  const { user } = useSelector((state) => state.auth);
  const { theme } = useSelector((state) => state.ui);
  const [period, setPeriod] = useState("3M");
  const [totalDeposits, setTotalDeposits] = useState(0);
  const responsive = useResponsive();

  const isDark = theme === "dark";

  // Responsive values
  const padding = {
    xs: "12px",
    sm: "14px",
    md: "16px",
    lg: "20px",
    xl: "24px",
  };

  const getPadding = () => {
    if (responsive.xs) return padding.xs;
    if (responsive.sm) return padding.sm;
    if (responsive.md) return padding.md;
    if (responsive.lg) return padding.lg;
    return padding.xl;
  };

  const getChartHeight = () => {
    if (responsive.xs) return 180;
    if (responsive.sm) return 200;
    if (responsive.md) return 240;
    if (responsive.lg) return 280;
    return 320;
  };

  const getMonthlyChartHeight = () => {
    if (responsive.xs) return 200;
    if (responsive.sm) return 220;
    if (responsive.md) return 260;
    if (responsive.lg) return 300;
    return 320;
  };

  const getDonutSize = () => {
    if (responsive.xs) return { size: 90, inner: 25, outer: 42 };
    if (responsive.sm) return { size: 100, inner: 30, outer: 48 };
    if (responsive.md) return { size: 120, inner: 35, outer: 55 };
    if (responsive.lg) return { size: 140, inner: 45, outer: 68 };
    return { size: 160, inner: 50, outer: 80 };
  };

  const getROIFontSize = () => {
    if (responsive.xs) return "24px";
    if (responsive.sm) return "28px";
    if (responsive.md) return "36px";
    if (responsive.lg) return "40px";
    return "48px";
  };

  const getMetricFontSize = () => {
    if (responsive.xs) return "14px";
    if (responsive.sm) return "15px";
    if (responsive.md) return "16px";
    if (responsive.lg) return "17px";
    return "18px";
  };

  const getHeadingFontSize = () => {
    if (responsive.xs) return "12px";
    if (responsive.sm) return "12px";
    if (responsive.md) return "13px";
    if (responsive.lg) return "14px";
    return "14px";
  };

  const getTotalFontSize = () => {
    if (responsive.xs) return "18px";
    if (responsive.sm) return "20px";
    if (responsive.md) return "22px";
    if (responsive.lg) return "24px";
    return "24px";
  };

  const getGridGap = () => {
    if (responsive.xs) return "8px";
    if (responsive.sm) return "10px";
    if (responsive.md) return "12px";
    if (responsive.lg) return "16px";
    return "24px";
  };

  const getCardGap = () => {
    if (responsive.xs) return "12px";
    if (responsive.sm) return "12px";
    if (responsive.md) return "16px";
    if (responsive.lg) return "20px";
    return "24px";
  };

  const getMetricsGridCols = () => {
    if (responsive.xs) return "1fr 1fr";
    if (responsive.sm) return "1fr 1fr";
    if (responsive.md) return "1fr 1fr 1fr";
    return "1fr 1fr 1fr";
  };

  const getTwoColGridCols = () => {
    if (responsive.xs) return "1fr";
    if (responsive.sm) return "1fr";
    if (responsive.md) return "1fr 1fr";
    return "1fr 1fr";
  };

  // Fetch total deposits
  useEffect(() => {
    api
      .get("/user/finance/summary")
      .then((r) => setTotalDeposits(Number(r.data.totalDeposits) || 0))
      .catch(() => setTotalDeposits(0));
  }, []);

  const colors = {
    bg: "var(--color-bg)",
    cardBg: "var(--color-bgSurface)",
    border: "var(--color-border)",
    text: "var(--color-text)",
    textSecondary: "var(--color-textSecondary)",
    green: isDark ? "#3fb950" : "#1a7f37",
    greenBg: isDark ? "rgba(63, 185, 80, 0.1)" : "rgba(26, 127, 55, 0.1)",
    gray: isDark ? "#8b949e" : "#57606a",
    grayBg: isDark ? "rgba(139, 148, 158, 0.1)" : "rgba(87, 96, 106, 0.1)",
    purple: isDark ? "#a78bfa" : "#7c3aed",
    purpleBg: isDark ? "rgba(167, 139, 250, 0.1)" : "rgba(124, 58, 237, 0.1)",
    orange: isDark ? "#d29922" : "#9e6a03",
    chartGrid: isDark ? "#30363d" : "#d0d7de",
    chartAxis: isDark ? "#8b949e" : "#57606a",
    tooltipBg: isDark ? "#161b22" : "#ffffff",
    tooltipBorder: isDark ? "#30363d" : "#d0d7de",
  };

  const filteredTrades = useMemo(() => {
    const now = new Date();
    let cutoffDate = new Date();

    if (period === "3M") {
      cutoffDate.setMonth(now.getMonth() - 3);
    } else if (period === "1Y") {
      cutoffDate.setFullYear(now.getFullYear() - 1);
    } else {
      cutoffDate = new Date(0);
    }

    return historyOrders.filter((trade) => {
      const tradeDate = new Date(
        trade.closeTime || trade.openTime || trade.time
      );
      return tradeDate >= cutoffDate;
    });
  }, [historyOrders, period]);

  const metrics = useMemo(() => {
    const trades = filteredTrades.filter(
      (t) => t && (t.pnl !== undefined || t.pnl !== null)
    );
    const totalTrades = trades.length;
    const winningTrades = trades.filter((t) => (Number(t.pnl) || 0) > 0);
    const losingTrades = trades.filter((t) => (Number(t.pnl) || 0) < 0);
    const grossProfit = winningTrades.reduce(
      (sum, t) => sum + (Number(t.pnl) || 0),
      0
    );
    const grossLoss = Math.abs(
      losingTrades.reduce((sum, t) => sum + (Number(t.pnl) || 0), 0)
    );
    const avgWinUSD =
      winningTrades.length > 0 ? grossProfit / winningTrades.length : 0;
    const avgLossUSD =
      losingTrades.length > 0 ? grossLoss / losingTrades.length : 0;
    const avgWinPoints =
      winningTrades.length > 0
        ? winningTrades.reduce(
            (sum, t) =>
              sum + Math.abs(Number(t.exitPrice) - Number(t.entryPrice)),
            0
          ) / winningTrades.length
        : 0;
    const avgLossPoints =
      losingTrades.length > 0
        ? losingTrades.reduce(
            (sum, t) =>
              sum + Math.abs(Number(t.exitPrice) - Number(t.entryPrice)),
            0
          ) / losingTrades.length
        : 0;
    const profitFactor =
      grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0;
    const totalPnl = trades.reduce((sum, t) => sum + (Number(t.pnl) || 0), 0);

    const totalDepositsValue =
      totalDeposits || Number(user?.totalDeposits) || 0;
    const roi =
      totalDepositsValue > 0 ? (totalPnl / totalDepositsValue) * 100 : 0;

    const buys = trades.filter((t) => t.direction === "buy");
    const sells = trades.filter((t) => t.direction === "sell");
    const profitableBuys = buys.filter((t) => (Number(t.pnl) || 0) > 0);
    const losingBuys = buys.filter((t) => (Number(t.pnl) || 0) < 0);
    const profitableSells = sells.filter((t) => (Number(t.pnl) || 0) > 0);
    const losingSells = sells.filter((t) => (Number(t.pnl) || 0) < 0);

    const dailyMap = {};
    trades.forEach((trade) => {
      const date = new Date(trade.closeTime || trade.openTime || trade.time);
      const dateKey = date.toISOString().split("T")[0];
      if (!dailyMap[dateKey]) dailyMap[dateKey] = { pnl: 0 };
      dailyMap[dateKey].pnl += Number(trade.pnl) || 0;
    });

    const dailyData = Object.entries(dailyMap)
      .map(([date, data]) => ({ date, pnl: data.pnl }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    const profitableDays = dailyData.filter((d) => d.pnl > 0).length;
    const losingDays = dailyData.filter((d) => d.pnl < 0).length;
    const totalDays = dailyData.length;

    const monthlyMap = {};
    trades.forEach((trade) => {
      const date = new Date(trade.closeTime || trade.openTime || trade.time);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      if (!monthlyMap[monthKey])
        monthlyMap[monthKey] = { pnl: 0, month: monthKey };
      monthlyMap[monthKey].pnl += Number(trade.pnl) || 0;
    });

    const monthlyData = Object.values(monthlyMap).sort((a, b) =>
      a.month.localeCompare(b.month)
    );

    return {
      totalTrades,
      totalPnl,
      grossProfit,
      grossLoss,
      profitFactor:
        profitFactor === Infinity ? "∞" : profitFactor.toFixed(2),
      avgWinUSD,
      avgLossUSD,
      avgWinPoints,
      avgLossPoints,
      roi,
      roiValue: totalPnl,
      totalDeposits: totalDepositsValue,
      buys: buys.length,
      sells: sells.length,
      profitableBuys: profitableBuys.length,
      losingBuys: losingBuys.length,
      profitableSells: profitableSells.length,
      losingSells: losingSells.length,
      buysPercent:
        buys.length > 0 ? (profitableBuys.length / buys.length) * 100 : 0,
      sellsPercent:
        sells.length > 0 ? (profitableSells.length / sells.length) * 100 : 0,
      dailyData,
      totalDays,
      profitableDays,
      losingDays,
      profitableDaysPercent:
        totalDays > 0 ? (profitableDays / totalDays) * 100 : 0,
      losingDaysPercent:
        totalDays > 0 ? (losingDays / totalDays) * 100 : 0,
      monthlyData,
      winRate:
        totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0,
    };
  }, [filteredTrades, user, totalDeposits]);

  const roiTrendData = useMemo(() => {
    const trades = filteredTrades.filter(
      (t) => t && (t.pnl !== undefined || t.pnl !== null)
    );
    const sortedTrades = [...trades].sort(
      (a, b) =>
        new Date(a.closeTime || a.openTime || a.time) -
        new Date(b.closeTime || b.openTime || b.time)
    );
    let cumulative = 0;
    return sortedTrades.map((trade) => {
      cumulative += Number(trade.pnl) || 0;
      return {
        date: new Date(
          trade.closeTime || trade.openTime || trade.time
        ).toLocaleDateString("en-US", { month: "short", year: "numeric" }),
        roi: cumulative,
      };
    });
  }, [filteredTrades]);

  const donutData = [
    {
      name: "Profitable Days",
      value: metrics.profitableDays,
      color: colors.green,
    },
    { name: "Losing Days", value: metrics.losingDays, color: colors.gray },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            background: colors.tooltipBg,
            border: `1px solid ${colors.tooltipBorder}`,
            borderRadius: "8px",
            padding: "12px",
            boxShadow: isDark
              ? "0 4px 12px rgba(0,0,0,0.3)"
              : "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <p
            style={{
              color: colors.text,
              fontWeight: "bold",
              margin: 0,
              fontFamily: "monospace",
              fontSize: "11px",
            }}
          >
            {label}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                color: colors.text,
                fontFamily: "monospace",
                margin: "2px 0",
                fontSize: "10px",
              }}
            >
              {entry.name}:{" "}
              {typeof entry.value === "number"
                ? fmtUSD(entry.value)
                : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const cardStyle = {
    background: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: "12px",
  };

  const labelStyle = {
    fontSize: "9px",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: colors.textSecondary,
    fontFamily: "monospace",
  };

  const valueStyle = {
    fontFamily: "monospace",
    fontWeight: 900,
  };

  const donutConfig = getDonutSize();

  return (
    <div style={{ background: colors.bg, minHeight: "100vh" }}>
      {!responsive.xs && <Topbar />}
      <div
        className="relative z-10"
        style={{
          padding: `${getPadding()} ${responsive.xs ? "10px" : getPadding()}`,
          paddingTop: responsive.xs ? getPadding() : `calc(52px + ${getPadding()})`,
        }}
      >
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* ROI Section */}
          <div
            style={{
              ...cardStyle,
              padding: getPadding(),
              marginBottom: getCardGap(),
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: responsive.isMobile ? "1fr" : "2fr 1fr",
                gap: getCardGap(),
                marginBottom: getCardGap(),
                alignItems: "flex-start",
              }}
            >
              <div>
                <div style={{ ...labelStyle, marginBottom: "8px" }}>ROI</div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      ...valueStyle,
                      fontSize: getROIFontSize(),
                      color:
                        metrics.roi >= 0 ? colors.green : colors.textSecondary,
                    }}
                  >
                    {metrics.roi >= 0 ? "+" : ""}
                    {metrics.roi.toFixed(2)}%
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: responsive.xs ? "11px" : "14px",
                      color: colors.textSecondary,
                    }}
                  >
                    {fmtUSD(metrics.roiValue)}
                  </span>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  justifyContent: responsive.isMobile
                    ? "flex-start"
                    : "flex-end",
                  flexWrap: "wrap",
                }}
              >
                {["3M", "1Y", "ALL"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    style={{
                      padding: responsive.xs
                        ? "6px 10px"
                        : responsive.sm
                          ? "6px 12px"
                          : "8px 16px",
                      borderRadius: "8px",
                      fontSize: responsive.xs ? "9px" : "10px",
                      fontWeight: "bold",
                      border: `1px solid ${colors.border}`,
                      background:
                        period === p ? colors.border : "transparent",
                      color:
                        period === p ? colors.text : colors.textSecondary,
                      cursor: "pointer",
                      transition: "all 0.2s",
                      fontFamily: "monospace",
                      minHeight: responsive.isMobile ? "36px" : "auto",
                      minWidth: responsive.isMobile ? "44px" : "auto",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* ROI Trend Chart */}
            <div style={{ height: getChartHeight() }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={roiTrendData}>
                  <defs>
                    <linearGradient id="roiGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor={colors.green}
                        stopOpacity={0.2}
                      />
                      <stop
                        offset="100%"
                        stopColor={colors.green}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={colors.chartGrid}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{
                      fontSize: responsive.xs ? 7 : 9,
                      fill: colors.chartAxis,
                      fontFamily: "monospace",
                      fontWeight: "bold",
                    }}
                    stroke={colors.chartGrid}
                    tickLine={false}
                    axisLine={{ stroke: colors.chartGrid }}
                  />
                  <YAxis
                    tick={{
                      fontSize: responsive.xs ? 7 : 9,
                      fill: colors.chartAxis,
                      fontFamily: "monospace",
                      fontWeight: "bold",
                    }}
                    stroke={colors.chartGrid}
                    tickLine={false}
                    axisLine={{ stroke: colors.chartGrid }}
                    tickFormatter={(value) => fmtUSD(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="roi"
                    stroke={colors.green}
                    strokeWidth={2}
                    fill="url(#roiGradient)"
                    dot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Metrics Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: getMetricsGridCols(),
              gap: getGridGap(),
              marginBottom: getCardGap(),
            }}
          >
            {/* Average Win */}
            <div
              style={{
                ...cardStyle,
                padding: getPadding(),
                gridColumn:
                  responsive.xs && "1 / -1"
                    ? "1 / -1"
                    : responsive.sm
                      ? "span 1"
                      : "span 1",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: colors.greenBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fas fa-arrow-up"
                    style={{ fontSize: "11px", color: colors.green }}
                  />
                </div>
                <span style={labelStyle}>Average Win</span>
              </div>
              <div
                style={{
                  ...valueStyle,
                  fontSize: getMetricFontSize(),
                  color: colors.green,
                  marginBottom: "8px",
                }}
              >
                {fmtUSD(metrics.avgWinUSD)}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "9px",
                  color: colors.textSecondary,
                }}
              >
                {metrics.avgWinPoints.toFixed(2)} POINTS
              </div>
            </div>

            {/* Average Loss */}
            <div
              style={{
                ...cardStyle,
                padding: getPadding(),
                gridColumn:
                  responsive.xs && "1 / -1"
                    ? "1 / -1"
                    : responsive.sm
                      ? "span 1"
                      : "span 1",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: colors.grayBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fas fa-arrow-down"
                    style={{ fontSize: "11px", color: colors.gray }}
                  />
                </div>
                <span style={labelStyle}>Average Loss</span>
              </div>
              <div
                style={{
                  ...valueStyle,
                  fontSize: getMetricFontSize(),
                  color: colors.textSecondary,
                  marginBottom: "8px",
                }}
              >
                {fmtUSD(metrics.avgLossUSD)}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "9px",
                  color: colors.textSecondary,
                }}
              >
                {metrics.avgLossPoints.toFixed(2)} POINTS
              </div>
            </div>

            {/* Profit Factor */}
            <div
              style={{
                ...cardStyle,
                padding: getPadding(),
                gridColumn: responsive.md || responsive.lg || responsive.xl ? "span 1" : "1 / -1",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "6px",
                    background: colors.purpleBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fas fa-scale-balanced"
                    style={{ fontSize: "11px", color: colors.purple }}
                  />
                </div>
                <span style={labelStyle}>Profit Factor</span>
              </div>
              <div
                style={{
                  ...valueStyle,
                  fontSize: getMetricFontSize(),
                  color: colors.text,
                  marginBottom: "8px",
                }}
              >
                {metrics.profitFactor}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "9px",
                  color: colors.textSecondary,
                }}
              >
                Gross Profit / Loss
              </div>
            </div>
          </div>

          {/* Trade Summary + Daily Performance */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: getTwoColGridCols(),
              gap: getCardGap(),
              marginBottom: getCardGap(),
            }}
          >
            {/* Trade Summary */}
            <div style={{ ...cardStyle, padding: getPadding() }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2
                  style={{
                    fontFamily: "monospace",
                    fontSize: getHeadingFontSize(),
                    fontWeight: 900,
                    color: colors.text,
                    margin: 0,
                  }}
                >
                  Trade summary
                </h2>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: getTotalFontSize(),
                    fontWeight: 900,
                    color: colors.orange,
                  }}
                >
                  {metrics.totalTrades}
                </span>
              </div>

              {/* Buys */}
              <div style={{ marginBottom: "24px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    gap: "8px",
                    flexWrap: responsive.isMobile ? "wrap" : "nowrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: responsive.xs ? "9px" : "10px",
                      fontWeight: "bold",
                      color: colors.text,
                    }}
                  >
                    {metrics.profitableBuys} buys (
                    {metrics.buysPercent.toFixed(1)}%)
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: responsive.xs ? "9px" : "10px",
                      fontWeight: "bold",
                      color: colors.textSecondary,
                    }}
                  >
                    {metrics.losingBuys} buys (
                    {(100 - metrics.buysPercent).toFixed(1)}%)
                  </span>
                </div>
                <div
                  style={{
                    height: "8px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    background: colors.bg,
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${metrics.buysPercent}%`,
                      background: colors.green,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontFamily: "monospace",
                    fontSize: "8px",
                  }}
                >
                  <span style={{ color: colors.green }}>Profitable Buys</span>
                  <span style={{ color: colors.textSecondary }}>
                    Losing Buys
                  </span>
                </div>
              </div>

              {/* Sells */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    gap: "8px",
                    flexWrap: responsive.isMobile ? "wrap" : "nowrap",
                  }}
                >
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: responsive.xs ? "9px" : "10px",
                      fontWeight: "bold",
                      color: colors.text,
                    }}
                  >
                    {metrics.profitableSells} sells (
                    {metrics.sellsPercent.toFixed(1)}%)
                  </span>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: responsive.xs ? "9px" : "10px",
                      fontWeight: "bold",
                      color: colors.textSecondary,
                    }}
                  >
                    {metrics.losingSells} sells (
                    {(100 - metrics.sellsPercent).toFixed(1)}%)
                  </span>
                </div>
                <div
                  style={{
                    height: "8px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    background: colors.bg,
                    marginBottom: "8px",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      width: `${metrics.sellsPercent}%`,
                      background: colors.green,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontFamily: "monospace",
                    fontSize: "8px",
                  }}
                >
                  <span style={{ color: colors.green }}>
                    Profitable Sells
                  </span>
                  <span style={{ color: colors.textSecondary }}>
                    Losing Sells
                  </span>
                </div>
              </div>
            </div>

            {/* Daily Performance */}
            <div style={{ ...cardStyle, padding: getPadding() }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <h2
                  style={{
                    fontFamily: "monospace",
                    fontSize: getHeadingFontSize(),
                    fontWeight: 900,
                    color: colors.text,
                    margin: 0,
                  }}
                >
                  Daily performance
                </h2>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: getTotalFontSize(),
                    fontWeight: 900,
                    color: colors.purple,
                  }}
                >
                  {metrics.totalDays}
                </span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: responsive.xs ? "12px" : getCardGap(),
                  flexWrap: responsive.isMobile ? "wrap" : "nowrap",
                }}
              >
                {/* Donut Chart */}
                <div
                  style={{
                    width: donutConfig.size,
                    height: donutConfig.size,
                    flexShrink: 0,
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={donutConfig.inner}
                        outerRadius={donutConfig.outer}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                      >
                        {donutData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={entry.color}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    minWidth: responsive.xs ? "100%" : "auto",
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: colors.green,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: "9px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          color: colors.textSecondary,
                        }}
                      >
                        Profitable Days
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize:
                          responsive.xs || responsive.sm
                            ? "14px"
                            : "20px",
                        fontWeight: 900,
                        color: colors.green,
                      }}
                    >
                      {metrics.profitableDays} days
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: "9px",
                        color: colors.textSecondary,
                        marginTop: "2px",
                      }}
                    >
                      ({metrics.profitableDaysPercent.toFixed(1)}%)
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <span
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: colors.gray,
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "monospace",
                          fontSize: "9px",
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          color: colors.textSecondary,
                        }}
                      >
                        Losing Days
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize:
                          responsive.xs || responsive.sm
                            ? "14px"
                            : "20px",
                        fontWeight: 900,
                        color: colors.textSecondary,
                      }}
                    >
                      {metrics.losingDays} days
                    </div>
                    <div
                      style={{
                        fontFamily: "monospace",
                        fontSize: "9px",
                        color: colors.textSecondary,
                        marginTop: "2px",
                      }}
                    >
                      ({metrics.losingDaysPercent.toFixed(1)}%)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly ROI */}
          <div style={{ ...cardStyle, padding: getPadding() }}>
            <h2
              style={{
                fontFamily: "monospace",
                fontSize: getHeadingFontSize(),
                fontWeight: 900,
                color: colors.text,
                margin: "0 0 20px 0",
              }}
            >
              Monthly ROI
            </h2>
            <div style={{ height: getMonthlyChartHeight() }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={metrics.monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={colors.chartGrid}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{
                      fontSize: responsive.xs ? 7 : 9,
                      fill: colors.chartAxis,
                      fontFamily: "monospace",
                      fontWeight: "bold",
                    }}
                    stroke={colors.chartGrid}
                    tickLine={false}
                    axisLine={{ stroke: colors.chartGrid }}
                    tickFormatter={(value) => {
                      const [year, month] = value.split("-");
                      return new Date(year, month - 1).toLocaleString(
                        "default",
                        { month: "short" }
                      );
                    }}
                  />
                  <YAxis
                    tick={{
                      fontSize: responsive.xs ? 7 : 9,
                      fill: colors.chartAxis,
                      fontFamily: "monospace",
                      fontWeight: "bold",
                    }}
                    stroke={colors.chartGrid}
                    tickLine={false}
                    axisLine={{ stroke: colors.chartGrid }}
                    tickFormatter={(value) => fmtUSD(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="pnl"
                    name="Monthly P&L"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    {metrics.monthlyData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          entry.pnl >= 0 ? colors.green : colors.gray
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      <Toast />
    </div>
  );
};

export default Analytics;